import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlane, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

const API_BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2;

const EmailModal = ({ isOpen, onClose, email, onVerificationSuccess }) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [btnDisable, setBtnDisable] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationLoading, setVerificationLoading] = useState(false);

  // Toast configuration
  const showToast = (message, type = "success") => {
    if (type === "success") {
      toast.success(message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (type === "error") {
      toast.error(message, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (type === "info") {
      toast.info(message, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Reset OTP when modal opens
  useEffect(() => {
    if (isOpen) {
      setOtp(["", "", "", "", "", ""]);
      setBtnDisable(false);
      setVerificationLoading(false);
      
      // Start 60-second cooldown for resend
      setResendCooldown(60);
    }
  }, [isOpen]);

  // Handle countdown for resend cooldown
  useEffect(() => {
    let interval;
    if (resendCooldown > 0 && isOpen) {
      interval = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown, isOpen]);

  // Validate OTP input
  const validateOtp = (value) => {
    // Only allow alphanumeric characters
    return /^[0-9A-Za-z]?$/.test(value);
  };

  // Handle OTP box input
  const handleInput = (value, index) => {
    if (!validateOtp(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.toUpperCase();
    setOtp(newOtp);

    // Auto move to next
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // Handle paste event
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^[0-9A-Za-z]{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('').map(char => char.toUpperCase());
      setOtp(newOtp);
      
      // Focus the last input
      setTimeout(() => {
        const lastInput = document.getElementById(`otp-5`);
        if (lastInput) lastInput.focus();
      }, 0);
    } else {
      showToast("Invalid OTP format. Please enter 6 alphanumeric characters.", "error");
    }
  };

  // Handle keydown for backspace
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };

  // Verify OTP
  const verifyOtp = async () => {
    const code = otp.join("");
    
    if (code.length !== 6) {
      showToast("Please enter the complete 6-digit code", "error");
      // Focus first empty field
      const emptyIndex = otp.findIndex(digit => !digit);
      const inputId = `otp-${emptyIndex >= 0 ? emptyIndex : 0}`;
      const inputElement = document.getElementById(inputId);
      if (inputElement) inputElement.focus();
      return;
    }

    // Validate OTP format
    if (!/^[0-9A-Za-z]{6}$/.test(code)) {
      showToast("Invalid OTP format. Only alphanumeric characters allowed.", "error");
      return;
    }

    setVerificationLoading(true);
    setBtnDisable(true);

    try {
      const res = await axios.post(`${API_BASE}/api/auth/verify-otp`, {
        email,
        otp: code,
      });

      if (res.data.success) {
        showToast("Email verified successfully! You can now login.", "success");
        
        // Call success callback if provided
        if (onVerificationSuccess) {
          onVerificationSuccess();
        }
        
        // Close modal after delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        showToast(res.data.message || "Invalid verification code", "error");
        // Clear OTP and focus first input on error
        setOtp(["", "", "", "", "", ""]);
        setTimeout(() => {
          const firstInput = document.getElementById("otp-0");
          if (firstInput) firstInput.focus();
        }, 100);
      }
    } catch (error) {
      console.error("Verification error:", error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          "Verification failed. Please try again.";
      showToast(errorMessage, "error");
      // Clear OTP on error
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => {
        const firstInput = document.getElementById("otp-0");
        if (firstInput) firstInput.focus();
      }, 100);
    } finally {
      setVerificationLoading(false);
      setBtnDisable(false);
    }
  };

  // Resend OTP
  const resendOtp = async () => {
    if (resendCooldown > 0) {
      showToast(`Please wait ${resendCooldown} seconds before resending`, "info");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast("Invalid email address", "error");
      return;
    }

    try {
      setBtnDisable(true);
      const res = await axios.post(`${API_BASE}/api/auth/resend-otp`, { email });

      if (res.data.success) {
        showToast("New verification code sent to your email!", "success");
        setResendCooldown(60); // Reset cooldown to 60 seconds
        setOtp(["", "", "", "", "", ""]); // Clear OTP fields
        
        // Focus first input
        setTimeout(() => {
          const firstInput = document.getElementById("otp-0");
          if (firstInput) firstInput.focus();
        }, 100);
      } else {
        showToast(res.data.message || "Failed to resend code", "error");
      }
    } catch (error) {
      console.error("Resend error:", error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          "Failed to resend verification code";
      showToast(errorMessage, "error");
    } finally {
      setBtnDisable(false);
    }
  };

  // Handle Enter key press for OTP verification
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && isOpen) {
        verifyOtp();
      }
    };

    if (isOpen) {
      document.addEventListener('keypress', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, [isOpen, otp, verificationLoading]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-[100] p-4">
      <motion.div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <FaTimes className="w-5 h-5" />
        </button>

        {/* Header with Gradient Background */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
          <div className="flex items-center justify-center mb-4">
            <motion.div
              initial={{ rotate: -10 }}
              animate={{ rotate: 0 }}
              transition={{ duration: 0.6 }}
            >
              <FaPlane className="w-10 h-10" />
            </motion.div>
          </div>
          <h2 className="text-2xl font-bold text-center mb-2">
            Verify Your Email ✉️
          </h2>
          <p className="text-center text-emerald-100 text-sm">
            Enter the 6-digit code sent to your email
          </p>
        </div>

        {/* Content Section */}
        <div className="p-6">
          <div className="text-center mb-6">
            <p className="text-gray-600 mb-2">
              We've sent a verification code to:
            </p>
            <p className="font-semibold text-lg text-gray-800 break-all">
              {email}
            </p>
            <p className="text-sm text-gray-500 mt-3">
              Enter the 6-digit alphanumeric code below to complete your registration
            </p>
            <p className="text-xs text-gray-400 mt-1">
              (Use numbers 0-9 or letters A-Z)
            </p>
          </div>

          {/* OTP Input Boxes */}
          <div className="flex justify-center gap-3 my-8">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInput(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-12 text-center border-2 border-gray-300 rounded-lg 
                         text-2xl font-bold focus:outline-none focus:border-green-500 
                         focus:ring-2 focus:ring-green-200 transition-all
                         disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={btnDisable || verificationLoading}
                autoFocus={index === 0}
                inputMode="text"
                autoComplete="one-time-code"
              />
            ))}
          </div>

          {/* Verify Button */}
          <button
            onClick={verifyOtp}
            disabled={btnDisable || verificationLoading}
            className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 
                     text-white font-semibold rounded-lg hover:from-green-700 
                     hover:to-emerald-700 transition-all duration-300 shadow-lg 
                     hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed
                     focus:outline-none focus:ring-2 focus:ring-green-300"
          >
            {verificationLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Verifying...
              </span>
            ) : (
              "Verify Email"
            )}
          </button>

          {/* Resend Code Section */}
          <div className="text-center mt-6">
            <p className="text-gray-600">
              Didn't receive the code?{" "}
              <button
                onClick={resendOtp}
                disabled={resendCooldown > 0 || btnDisable || verificationLoading}
                className={`font-semibold ${
                  resendCooldown > 0 || btnDisable || verificationLoading
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-emerald-600 hover:text-emerald-700 hover:underline"
                } transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300 rounded px-1`}
              >
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Resend Code"}
              </button>
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Check your spam folder if you don't see the email
            </p>
          </div>

          {/* Manual email entry fallback */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              Using a different email?{" "}
              <button
                onClick={onClose}
                className="text-blue-600 hover:text-blue-700 font-medium hover:underline
                         focus:outline-none focus:ring-2 focus:ring-blue-300 rounded px-1"
              >
                Go back and update
              </button>
            </p>
          </div>

        </div>
      </motion.div>
    </div>
  );
};

export default EmailModal;